import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { fetchAnalyticsData } from '../../services/analyticsService';
import QuickActionToolbar from '../../components/navigation/QuickActionToolbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const PostDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshingAnalytics, setRefreshingAnalytics] = useState(false);

  useEffect(() => {
    loadPostDetails();
  }, [id]);

  const loadPostDetails = async () => {
    try {
      setLoading(true);
      
      // Get post details from database
      const { data: postData, error: postError } = await supabase
        .from('content_posts')
        .select(`
          *
        `)
        .eq('id', id)
        .single();

      if (postError) {
        console.error('Error loading post details:', postError);
        navigate('/dashboard');
        return;
      }

      setPost(postData);
    } catch (error) {
      console.error('Error loading post details:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const refreshAnalytics = async () => {
    try {
      setRefreshingAnalytics(true);
      await fetchAnalyticsData(true); // Force refresh
      await loadPostDetails(); // Reload post data
    } catch (error) {
      console.error('Error refreshing analytics:', error);
    } finally {
      setRefreshingAnalytics(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num?.toString() || '0';
  };

  // Helper function to convert HTML content to readable text
  const stripHtmlAndDecodeEntities = (html) => {
    if (!html) return '';
    
    try {
      // Create a temporary div to parse HTML
      const div = document.createElement('div');
      div.innerHTML = html;
      
      // Process the content to preserve some structure
      const processNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent;
        }
        
        if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName.toLowerCase();
          const childText = Array.from(node.childNodes).map(processNode).join('');
          
          // Add line breaks for block elements
          if (['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li'].includes(tagName)) {
            return childText + '\n';
          }
          
          // Add line breaks for br tags
          if (tagName === 'br') {
            return '\n';
          }
          
          return childText;
        }
        
        return '';
      };
      
      const text = processNode(div);
      
      // Clean up the text
      return text
        .replace(/&nbsp;/g, ' ') // Replace &nbsp; with spaces
        .replace(/\n\s*\n/g, '\n') // Replace multiple line breaks with single
        .replace(/[ \t]+/g, ' ') // Replace multiple spaces/tabs with single space
        .trim(); // Remove leading/trailing whitespace
        
    } catch (error) {
      // Fallback: basic HTML tag removal and entity decoding
      return html
        .replace(/<\/?(p|div|h[1-6]|li|br)[^>]*>/gi, '\n') // Replace block elements with line breaks
        .replace(/<[^>]*>/g, '') // Remove remaining HTML tags
        .replace(/&nbsp;/g, ' ') // Replace &nbsp; with spaces
        .replace(/&amp;/g, '&') // Decode &amp;
        .replace(/&lt;/g, '<') // Decode &lt;
        .replace(/&gt;/g, '>') // Decode &gt;
        .replace(/&quot;/g, '"') // Decode &quot;
        .replace(/&#39;/g, "'") // Decode &#39;
        .replace(/\n\s*\n/g, '\n') // Replace multiple line breaks
        .replace(/[ \t]+/g, ' ') // Replace multiple spaces
        .trim();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <QuickActionToolbar />
        <main className="pt-[60px] px-4 md:px-6 lg:px-8 pb-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <QuickActionToolbar />
        <main className="pt-[60px] px-4 md:px-6 lg:px-8 pb-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-12">
              <h1 className="text-2xl font-bold text-foreground mb-4">Post Not Found</h1>
              <Button onClick={() => navigate('/dashboard')}>
                Return to Dashboard
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <QuickActionToolbar />
      <main className="pt-[60px] px-4 md:px-6 lg:px-8 pb-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                iconName="ArrowLeft"
                onClick={() => navigate('/dashboard')}
              >
                Back to Dashboard
              </Button>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Post Details</h1>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName={refreshingAnalytics ? "Loader" : "RefreshCw"}
              onClick={refreshAnalytics}
              disabled={refreshingAnalytics}
            >
              {refreshingAnalytics ? 'Refreshing...' : 'Refresh Analytics'}
            </Button>
          </div>

          {/* Post Info */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Post Information</h2>
              <span className="text-sm text-muted-foreground">
                Created {formatDate(post.created_at)}
              </span>
            </div>
            
            {post.original_idea && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-foreground mb-2">Original Idea</h3>
                <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
                  <p className="whitespace-pre-wrap">
                    {stripHtmlAndDecodeEntities(post.original_idea)}
                  </p>
                </div>
              </div>
            )}

            {post.code_snippet && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-foreground mb-2">Code Snippet</h3>
                <pre className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3 overflow-x-auto">
                  <code>{post.code_snippet}</code>
                </pre>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-foreground mb-1">AI Model</h3>
                <p className="text-sm text-muted-foreground">{post.ai_model || 'Not specified'}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-foreground mb-1">Writing Style</h3>
                <p className="text-sm text-muted-foreground">{post.writing_style || 'Not specified'}</p>
              </div>
            </div>
          </div>

          {/* Platform Posts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* LinkedIn Post */}
            {post.linkedin_content && (
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#0A66C2]/20 flex items-center justify-center">
                    <Icon name="Linkedin" size={20} color="#0A66C2" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">LinkedIn</h3>
                    {post.linkedin_post_id && (
                      <p className="text-xs text-muted-foreground">Post ID: {post.linkedin_post_id}</p>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="text-sm font-medium text-foreground mb-2">Content</h4>
                  <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3 max-h-40 overflow-y-auto">
                    {post.linkedin_content.split('\n').map((line, index) => (
                      <p key={index} className={line.trim() ? '' : 'h-4'}>{line}</p>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground">
                      {formatNumber(post.linkedin_impressions || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground">Impressions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground">
                      {formatNumber(post.linkedin_engagement_count || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground">Engagement</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground">
                      {formatNumber(post.linkedin_reach || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground">Reach</div>
                  </div>
                </div>
              </div>
            )}

            {/* X/Twitter Post */}
            {post.twitter_content && (
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-black/20 flex items-center justify-center">
                    <Icon name="X" size={20} color="#000000" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">X (Twitter)</h3>
                    {post.twitter_post_id && (
                      <p className="text-xs text-muted-foreground">Post ID: {post.twitter_post_id}</p>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="text-sm font-medium text-foreground mb-2">Content</h4>
                  <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3 max-h-40 overflow-y-auto">
                    {post.twitter_content.split('\n').map((line, index) => (
                      <p key={index} className={line.trim() ? '' : 'h-4'}>{line}</p>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground">
                      {formatNumber(post.twitter_impressions || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground">Impressions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground">
                      {formatNumber(post.twitter_engagement_count || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground">Engagement</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground">
                      {formatNumber(post.twitter_reach || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground">Reach</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Analytics Summary */}
          {(post.linkedin_post_id || post.twitter_post_id) && (
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">Analytics Summary</h2>
                {post.last_analytics_fetch && (
                  <span className="text-xs text-muted-foreground">
                    Last updated: {formatDate(post.last_analytics_fetch)}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground mb-1">
                    {formatNumber((post.linkedin_impressions || 0) + (post.twitter_impressions || 0))}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Impressions</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground mb-1">
                    {formatNumber((post.linkedin_engagement_count || 0) + (post.twitter_engagement_count || 0))}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Engagement</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground mb-1">
                    {formatNumber((post.linkedin_reach || 0) + (post.twitter_reach || 0))}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Reach</div>
                </div>
              </div>

              {((post.linkedin_impressions || 0) + (post.twitter_impressions || 0)) > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-foreground">
                      {(((post.linkedin_engagement_count || 0) + (post.twitter_engagement_count || 0)) / 
                        ((post.linkedin_impressions || 0) + (post.twitter_impressions || 0)) * 100).toFixed(2)}%
                    </div>
                    <div className="text-sm text-muted-foreground">Engagement Rate</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PostDetails;